import { useEffect, useState } from "react";
import { Download, X, Share, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  isStandalone,
  hasInstallPrompt,
  onInstallPromptChange,
  triggerInstall,
  isIOS,
} from "@/lib/pwa";

const STORAGE_KEY = "kiddo_install_prompt_dismissed_at";
const DISMISS_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
const DELAY_MS = 10_000; // show after 10s as requested

/**
 * Mobile-friendly "Install App" banner shown after 10s of using the site.
 *
 * - Hides when already installed (standalone display-mode).
 * - On Android/desktop Chrome: triggers native install via beforeinstallprompt.
 * - On iOS Safari: shows the manual Add-to-Home-Screen instructions because
 *   iOS doesn't expose a programmatic install prompt.
 * - Remembers dismiss for 7 days.
 */
export default function InstallPrompt() {
  const [ready, setReady] = useState(false);   // true after the 10s delay
  const [available, setAvailable] = useState(hasInstallPrompt());
  const [iosOpen, setIosOpen] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    const dismissed = Number(localStorage.getItem(STORAGE_KEY) || 0);
    if (Date.now() - dismissed < DISMISS_COOLDOWN_MS) return;

    const t = setTimeout(() => setReady(true), DELAY_MS);
    const off = onInstallPromptChange((avail) => setAvailable(avail));
    return () => { clearTimeout(t); off(); };
  }, []);

  if (!ready) return null;
  if (isStandalone()) return null;

  // iOS path: show the "Tap Share → Add to Home Screen" hint.
  const iosMode = isIOS() && !available;
  if (!available && !iosMode) return null;

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, String(Date.now()));
    setReady(false);
    setIosOpen(false);
  };

  const install = async () => {
    if (iosMode) { setIosOpen(true); return; }
    const outcome = await triggerInstall();
    if (outcome !== "accepted") dismiss();
  };

  return (
    <>
      <div
        className="fixed inset-x-3 bottom-3 z-[80] sm:bottom-5 sm:left-auto sm:right-5 sm:max-w-sm animate-fade-in-up"
        role="dialog"
        aria-label="Install Kiddo+ app"
        data-testid="install-prompt"
      >
        <div className="bg-white border border-plum/10 shadow-soft-md rounded-3xl p-4 sm:p-5 flex items-start gap-3">
          <img src="/kiddo-logo-96.png" alt="" className="w-12 h-12 rounded-[28%] shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="font-heading text-base sm:text-lg leading-tight text-inkdark">Install Kiddo+</p>
            <p className="text-xs sm:text-sm text-inkmid mt-1 leading-snug">
              Install Kiddo+ for faster access, offline use, important updates, and a full app-like experience right from your home screen.
            </p>
            <div className="flex items-center gap-2 mt-3">
              <Button
                onClick={install}
                className="bg-coral hover:bg-coral-dark text-white rounded-full font-bold h-9 px-4 text-sm gap-1.5"
                data-testid="install-app-btn"
              >
                <Download className="w-3.5 h-3.5" /> Install
              </Button>
              <button
                onClick={dismiss}
                className="text-xs text-inkmid hover:text-inkdark font-semibold px-2 py-1"
                data-testid="install-later-btn"
              >
                Not now
              </button>
            </div>
          </div>
          <button
            onClick={dismiss}
            aria-label="Dismiss install prompt"
            className="text-inkmid hover:text-inkdark shrink-0 -mr-1 -mt-1 p-1"
            data-testid="install-dismiss-btn"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {iosOpen && (
        <div
          className="fixed inset-0 z-[90] bg-black/45 flex items-end sm:items-center justify-center p-4"
          onClick={() => setIosOpen(false)}
          data-testid="ios-install-modal"
        >
          <div
            className="bg-white rounded-3xl w-full max-w-md p-6 shadow-soft-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-3">
              <img src="/kiddo-logo-96.png" alt="" className="w-10 h-10 rounded-[28%]" />
              <h3 className="font-heading text-xl">Install on iPhone</h3>
            </div>
            <ol className="space-y-3 text-sm text-inkdark">
              <li className="flex items-start gap-2">
                <Share className="w-4 h-4 mt-0.5 text-coral shrink-0" />
                <span>Tap the <strong>Share</strong> button in Safari&apos;s toolbar.</span>
              </li>
              <li className="flex items-start gap-2">
                <Plus className="w-4 h-4 mt-0.5 text-coral shrink-0" />
                <span>Scroll down and tap <strong>Add to Home Screen</strong>.</span>
              </li>
              <li className="flex items-start gap-2">
                <Download className="w-4 h-4 mt-0.5 text-coral shrink-0" />
                <span>Tap <strong>Add</strong> — Kiddo+ now launches like an app.</span>
              </li>
            </ol>
            <Button
              onClick={dismiss}
              className="w-full mt-5 bg-coral hover:bg-coral-dark text-white rounded-full font-bold"
              data-testid="ios-install-close"
            >
              Got it
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
